import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { Representative } from 'src/app/shared/models/dtos/register-form/representative/representative';
import { RepresentativeRole } from 'src/app/shared/models/dtos/register-form/representative/representative-role';
import { InviteRepresentativeResponseDTO } from 'src/app/shared/models/dtos/responses/invite-representative-response-dto/invite-representative-response-dto';
import {
  GeneralizedRepresentative,
  isRepresentative,
} from 'src/app/shared/models/generalized-representative/generalized-representative';
import { RepresentativeItemLabels } from 'src/app/shared/models/representative-item-labels/representative-item-labels';
import { AuthenticatedUserService } from 'src/app/core/services/authenticated-user/authenticated-user.service';

@Component({
  selector: 'app-representative-item',
  templateUrl: './representative-item.component.html',
  styleUrls: ['./representative-item.component.scss'],
})
/**
 * Item of a representative list
 */
export class RepresentativeItemComponent implements OnInit {
  @Input() generalizedRepresentative!: GeneralizedRepresentative;
  @Input() labels!: RepresentativeItemLabels;
  @Input() expiredTip?: string;
  @Input() canTamper?: boolean;
  @Input() isSummary?: boolean;
  @Input() isSingleAdmin!: boolean;
  @Output() promote = new EventEmitter<Representative>();
  @Output() remove = new EventEmitter<GeneralizedRepresentative>();
  @Output() resendInvite = new EventEmitter<InviteRepresentativeResponseDTO>();
  alt = 'uploaded-image-alt';
  viewOnly!: boolean;

  /**
   * @constructor
   */
  constructor(
    private translationService: TranslationService,
    private authenticatedUserService: AuthenticatedUserService
  ) {}

  /**
   * @ngOnInit
   */
  ngOnInit(): void {
    this.viewOnly =
      this.authenticatedUserService.isVOAdmin() ||
      this.authenticatedUserService.isCallCenterAgent();
    this.translationService.translateString(this.alt).then((translated) => {
      this.alt = translated;
    });
  }

  /**
   * If an invitation for business representative has expired
   * @return {boolean}
   */
  isExpired(): boolean {
    if (!isRepresentative(this.generalizedRepresentative)) {
      return this.generalizedRepresentative.expired;
    } else {
      return false;
    }
  }

  /**
   * If this representative is an admin
   * @return {boolean}
   */
  isAdmin(): boolean {
    if (isRepresentative(this.generalizedRepresentative)) {
      return Boolean(
        this.generalizedRepresentative.role ===
          RepresentativeRole.BUSINESS_ADMIN
      );
    } else {
      return false;
    }
  }

  /**
   * Wheter this generalized representative is a representative or not
   * Cannot be used as type guard
   * @return {boolean}
   */
  isRepresentative() {
    return isRepresentative(this.generalizedRepresentative);
  }

  /**
   * @return {string}
   */
  getAlt(): string {
    const alt = this.isExpired() ? this.expiredTip : '';
    return alt || '';
  }

  /**
   * Emits promotion event
   */
  doPromote() {
    if (isRepresentative(this.generalizedRepresentative)) {
      this.promote.emit(this.generalizedRepresentative);
    }
  }

  /**
   * Emits remove event
   */
  doRemove() {
    this.remove.emit(this.generalizedRepresentative);
  }

  /**
   * @param {InviteRepresentativeResponseDTO} invitee
   */
  doResendInvite() {
    if (!isRepresentative(this.generalizedRepresentative)) {
      this.resendInvite.emit(this.generalizedRepresentative);
    }
  }

  /**
   * Builds title, which depends on being admin or not
   * @return {string}
   */
  getTitle(): string {
    if (isRepresentative(this.generalizedRepresentative)) {
      return (
        `${this.generalizedRepresentative.firstName} ${this.generalizedRepresentative.lastName}` +
        (this.isAdmin() ? ' | Admin' : '')
      );
    } else {
      return '';
    }
  }

  /**
   * Checks if admin can be deleted
   * @return {boolean}
   */
  canDeleteRepresentative(): boolean {
    return !this.isAdmin() || this.isSingleAdmin;
  }
}
